// NPM MODULES
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Grid,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Stack,
  Link,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { ChevronLeft } from '@mui/icons-material';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';

// FORMATTED MESSAGES
import ScheduledFormattedMessages from '../../../messages/websms/ScheduledFormattedMessages';

// NETWORK
import useScheduledRequest from '../../../requests/ScheduledRequest';

// COMPONENTS
import SectionWrapper from '../../common/SectionWrapper';
import ConfirmationDialog from '../sent/ConfirmationDialog';
import Paginator from '../../common/Paginator';
import ScheduledSingle from './ScheduledSingle';

// CONFIG
import config from '../../../Config';

// UTILS
import {
  getZoneOffsettedDate,
  formatDateTime,
  getTimeIdentifier,
} from '../../../utils/dates';

const SentScheduledGroup = ({
  groupId,
  handleBackButton,
  failed = false,
  errorHandler,
}) => {
  const [justCancelled, setJustCancelled] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [smsGroup, setSmsGroup] = useState(null);
  const [cancelList, setCancelList] = useState(new Set());
  const [pageNumber, setPageNumber] = useState(1);
  const [pageLength, setPageLength] = useState(config.defaultPL);
  const [rowCount, setRowCount] = useState(0);
  const [messageSelected, setMessageSelected] = useState(false);
  const [isSearchingPage, setIsSearchingPage] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const {
    cancelAllPendingMessages,
    getScheduledItemsForGroup,
    rescheduleMessageGroup,
    cancelMessagesByGroup,
  } = useScheduledRequest();

  const fetchSmsGroup = async (isSearchingPage) => {
    if (isSearchingPage) setIsSearchingPage(true);
    else setSmsGroup(null);

    const smsGroup = await getScheduledItemsForGroup(
      groupId,
      pageNumber,
      pageLength,
      errorHandler
    );

    setSmsGroup(smsGroup);
    setRowCount(smsGroup.rowCount);
    setPendingCount(
      smsGroup.items.reduce((acc, item) => {
        if (item.sent_flag === 'N') return acc + 1;
        return acc;
      }, 0)
    );
    setIsSearchingPage(false);
    setCancelList(new Set());
  };

  useEffect(() => {
    fetchSmsGroup();
  }, [groupId]);

  useEffect(() => {
    fetchSmsGroup(true);
  }, [pageNumber, pageLength]);

  const handleCancelPendingMessage = async () => {
    setShowConfirmation(false);
    try {
      let result;

      // Cancels via list of SMS IDs
      if (cancelList.size > 0) {
        result = await cancelAllPendingMessages(
          Array.from(cancelList),
          errorHandler
        );

        // Else cancels for the entire group/email_id
      } else {
        result = await cancelMessagesByGroup([groupId], errorHandler);
      }

      if (result && result.length > 0 && setJustCancelled)
        setJustCancelled(true);

      fetchSmsGroup();
    } catch (e) {
      errorHandler(e.message);
    }
  };

  const changePageLength = (e) => {
    const value = e.target.value;
    setPageLength(value);
    setPageNumber(1);
    setCancelList(new Set());
  };

  const changePageEvent = (page) => {
    // Uses useEffect to fetch the page after changing state.
    setPageNumber(page);
    setCancelList(new Set());
  };

  const handleCheckboxChange = (event, id) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;

    handleCancelList(id, !value);
  };

  const handleCancelList = (smsId, remove) => {
    // Checks all entries on this page
    if (smsId === 'visible') {
      const newCancelList = remove
        ? new Set()
        : new Set(
            smsGroup.items
              .filter((item) => item.sent_flag === 'N')
              .map((item) => item.sms_id)
          );
      setCancelList(newCancelList);
    } else {
      const newCancelList = new Set(cancelList);
      if (remove) newCancelList.delete(smsId);
      else newCancelList.add(smsId);
      setCancelList(newCancelList);
    }

    console.log('cancelList:', cancelList);
  };

  const handleRescheduleMessages = async () => {
    console.log('reschedule messages');
    const result = await rescheduleMessageGroup(
      groupId,
      {
        sms_sent: rescheduleDate,
      },
      errorHandler
    );
    setShowReschedule(false);
    fetchSmsGroup();
  };

  const generateRescheduleDialog = () => {
    const timeIdentifier = getTimeIdentifier();
    return (
      <Dialog
        open={showReschedule}
        onClose={() => setShowReschedule(false)}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
      >
        <DialogTitle id="cancel-dialog-title" variant="h2">
          RESCHEDULE GROUP MESSAGE DETAILS
        </DialogTitle>

        <DialogContent>
          <DialogContentText id="cancel-dialog-description" variant="p">
            {'Reschedule the group of messages to a new date and time'}
            <br />
            <br />
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <DateTimePicker
                format="dd/MMM/yyyy h:mm a"
                defaultValue={DateTime.fromJSDate(rescheduleDate).setZone(
                  timeIdentifier
                )}
                timezone={timeIdentifier}
                fullWidth
                onChange={(value) => {
                  setRescheduleDate(value.toJSDate());
                }}
                minDate={DateTime.now()}
                views={['year', 'month', 'day', 'hours', 'minutes']}
                slotProps={{
                  textField: {
                    size: 'small',
                  },
                }}
              />
            </LocalizationProvider>
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setShowReschedule(false)}
            color="secondary"
            variant="outlined"
          >
            Cancel
          </Button>

          <Button
            onClick={handleRescheduleMessages}
            color="secondary"
            variant="contained"
            autoFocus
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderScheduledTable = () => {
    if (isSearchingPage) {
      return (
        <TableRow>
          <TableCell colSpan={7} sx={{ paddingY: 2 }}>
            <Typography>{ScheduledFormattedMessages.searchingText}</Typography>
          </TableCell>
        </TableRow>
      );
    }

    if (smsGroup && smsGroup.items) {
      return smsGroup.items.map((message, index) => (
        <TableRow key={index}>
          <TableCell>{getZoneOffsettedDate(message.sms_sent)}</TableCell>
          <TableCell>{message.dest_tn || 'N/A'}</TableCell>
          <TableCell>{message.dest_name || 'N/A'}</TableCell>
          <TableCell>{message.status || 'N/A'}</TableCell>
          <TableCell>
            <Link
              onClick={() => setMessageSelected(message.sms_id)}
              tabIndex={0}
            >
              {decodeURIComponent(message.sms_text)}
            </Link>
          </TableCell>
          <TableCell>
            <Checkbox
              value={message.sms_id}
              onChange={(event) => handleCheckboxChange(event, message.sms_id)}
              sx={{ padding: 0 }}
              checked={cancelList.has(message.sms_id)}
              disabled={message.sent_flag === 'C'}
            />
          </TableCell>
        </TableRow>
      ));
    }
  };

  const closeConfirmation = () => {
    setShowConfirmation(false);
    setJustCancelled(false);
  };

  const generateCancelPendingButton = () => {
    return (
      <Box>
        <Button
          className="maxHeight float-right"
          variant="outlined"
          color="secondary"
          type="submit"
          onClick={() => setShowConfirmation(!showConfirmation)}
          sx={{ backgroundColor: 'common.white' }}
          disabled={cancelList.size === 0}
        >
          {ScheduledFormattedMessages.cancelSelectedPendingMessages}
        </Button>
      </Box>
    );
  };

  const generateBody = () => {
    if (smsGroup) {
      let smsText;
      try {
        smsText = decodeURIComponent(smsGroup.parent_sms_text);
      } catch (err) {
        smsText = 'ERROR: MALFORMED TEXT';
      }

      return (
        <Box>
          <Typography gutterBottom>
            <strong>{ScheduledFormattedMessages.queuedHeader}</strong>
            {`: ${formatDateTime(smsGroup.sms_sent, true)}`}
          </Typography>

          <Typography gutterBottom>
            <strong>{'Message'}</strong>
            {`: ${smsText}`}
          </Typography>

          <Typography gutterBottom>
            <strong>{'Scheduled on'}</strong>
            {`: ${formatDateTime(smsGroup.created_date, true)}`}
          </Typography>

          <Typography gutterBottom>
            <strong>{'Modified on'}</strong>
            {`: ${formatDateTime(smsGroup.updated_date, true)}`}
          </Typography>

          {!justCancelled && (
            <Stack sx={{ flexDirection: 'row', gap: 2 }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setShowConfirmation(true)}
                sx={{ marginY: 2 }}
              >
                {'Cancel Messages'}
              </Button>

              <Button
                variant="contained"
                color="secondary"
                onClick={() => setShowReschedule(true)}
                sx={{ marginY: 2 }}
              >
                {'Reschedule Messages'}
              </Button>
            </Stack>
          )}
        </Box>
      );
    }

    return <CircularProgress />;
  };

  const backToScheduledGroup = () => {
    setMessageSelected(false);
    fetchSmsGroup();
    setPageNumber(1);
  };

  const generateTable = () => {
    if (!smsGroup) return;

    return (
      <Grid size={12}>
        <Stack
          sx={{
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
            marginBottom: 1,
          }}
        >
          {/* {filteredText && filteredText.length !== 0 && (
              <Typography fontSize={12}>{filteredText}</Typography>
            )} */}
          <Stack direction="row" spacing={2}>
            {generateCancelPendingButton()}
          </Stack>
        </Stack>

        <TableContainer
          sx={{
            marginBottom: 4,
            overflowX: 'auto',
            width: '100%',
          }}
        >
          <Table
            variant="rounded"
            sx={{
              minWidth: 1000,
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell width="20%">
                  {ScheduledFormattedMessages.dateTimeText}
                </TableCell>
                <TableCell width="20%">{'To'}</TableCell>
                <TableCell width="20%">{'Name'}</TableCell>
                <TableCell width="20%">{'Status'}</TableCell>
                <TableCell width="35%">{'Message'}</TableCell>
                <TableCell width="5%" sx={{ color: 'grey.700' }}>
                  <Checkbox
                    value={'visible'}
                    onChange={(event) => handleCheckboxChange(event, 'visible')}
                    sx={{ padding: 0 }}
                    checked={pendingCount && cancelList.size === pendingCount}
                  />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{renderScheduledTable()}</TableBody>
          </Table>
        </TableContainer>

        <Paginator
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          itemCount={rowCount ?? 0}
          changeEvent={changePageEvent}
          pageLength={pageLength}
          editFunction={(e) => changePageLength(e)}
          showTotal={'Found'}
          toolTip={'Maximum of 4,000 records shown'}
        />
      </Grid>
    );
  };

  if (messageSelected) {
    return (
      <ScheduledSingle
        smsId={messageSelected}
        handleBackButton={backToScheduledGroup}
        errorHandler={errorHandler}
      />
    );
  }

  return (
    <Grid container sx={{ justifyContent: 'space-between', rowGap: 2 }}>
      <Grid size={{ xs: 12, lg: 9 }}>
        <SectionWrapper title="SMS Information" icon="schedule">
          <Box>
            <Button
              startIcon={<ChevronLeft />}
              onClick={handleBackButton}
              color="secondary"
              sx={{ marginBottom: 2 }}
            >
              <Typography>
                {failed
                  ? ScheduledFormattedMessages.backFailedText
                  : ScheduledFormattedMessages.backText}
              </Typography>
            </Button>
          </Box>

          <Typography variant="h3" gutterBottom>
            {ScheduledFormattedMessages.replyDetails}
          </Typography>

          {generateBody()}

          <ConfirmationDialog
            showConfirmation={showConfirmation}
            setShowConfirmation={closeConfirmation}
            setJustCancelled={setJustCancelled}
            message={
              'Are you sure you would like to cancel this pending message?'
            }
            handleCancelPendingMessage={handleCancelPendingMessage}
          />
          {generateRescheduleDialog()}
        </SectionWrapper>
      </Grid>
      {generateTable()}
    </Grid>
  );
};

export default SentScheduledGroup;
